import { Route } from '@angular/router';
import { ModuleRoutes, QrQuestionComponent } from '@zayan-erp/shared';
import { AdminGuard } from './shared/guards/admin.guard';
import { ProtectedGuard } from './shared/guards/protected.guard';
import { PublicGuard } from './shared/guards/public.guard';
import { RoleAccessGuard } from './shared/guards/role-access.guard';
import { WelcomeScreenComponent } from './welcome-screen/welcome-screen.component';
import { SsoCrmComponent } from './sso-crm/sso-crm.component';
import { PageNotFoundComponent } from '../../../../libs/shared/src/lib/components/page-not-found/page-not-found.component';
import {
  executiveConsoleResolver,
  HomebuildingResolver,
  salesResolver,
  warrantyResolver
} from './shared/resolver/resolver';
import { UnauthLayoutComponent } from './public-module/unauth-layout/unauth-layout.component';
import { PublicProspectComponent } from './public-module/public-prospect/public-prospect.component';
import { publicPagesResolver } from './public-module/resolvers/public-pages.resolvers';
import { PublicRealtorComponent } from '@zayan-erp/sales';
import { themeGuard } from './shared/guards/theme.guard';

export const routes: Route[] = [
  {
    path: '',
    loadChildren: () => import('./authentication-module/auth.routes').then(m => m.AUTH_ROUTES),
    canActivate: [PublicGuard],
    canActivateChild: [themeGuard('legacy')]
  },
  {
    path: 'welcome',
    component: WelcomeScreenComponent,
    canActivate: [ProtectedGuard, themeGuard('legacy')],
    title: 'TH Portal | Welcome'
  },
  {
    path: 'sso/crm',
    component: SsoCrmComponent,
    canActivate: [ProtectedGuard, themeGuard('legacy')],
    title: 'TH Portal | CRM'
  },
  {
    path: 'knowledge-base',
    loadChildren: () => import('./knowledge-base-module/knowledge.routes').then(m => m.KNOWLEDGE_ROUTES),
    canActivate: [ProtectedGuard],
    canActivateChild: [themeGuard('legacy')],
    title: 'TH Portal | Knowledge Base'
  },
  {
    path: 'share',
    loadChildren: () => import('./public-module/public.routes').then(m => m.PUBLIC_ROUTES),
    canActivateChild: [themeGuard('legacy')]
  },
  {
    // Super-admin only release notes.
    path: 'whats-new',
    loadChildren: () => import('./whats-new-module/whats-new.routes').then(m => m.WHATS_NEW_ROUTES),
    canActivate: [ProtectedGuard, AdminGuard],
    canActivateChild: [themeGuard('primeng')]
  },
  {
    path: 'all-files',
    loadChildren: () => import('./dashboard-module/dashboard.routes').then(m => m.DASHBOARD_ROUTES),
    canActivate: [ProtectedGuard, RoleAccessGuard],
    canActivateChild: [themeGuard('legacy')],
    data: { module: ModuleRoutes.FileManager }
  },
  {
    path: 'admin',
    loadChildren: () => import('./admin-console-module/admin-console.routes').then(m => m.ADMIN_CONSOLE_ROUTES),
    canActivate: [AdminGuard],
    canActivateChild: [themeGuard('legacy')],
    data: { module: ModuleRoutes.FileManagerAdmin }
  },
  {
    // Manual test panel for the new realtime connection layer: connect/disconnect, echo,
    // and trigger a server-side publish. Admin-guarded since it's a diagnostics tool.
    path: 'realtime-diagnostics',
    loadComponent: () => import('@zayan-erp/realtime').then(m => m.RealtimeDiagnosticsComponent),
    canActivate: [AdminGuard],
    canActivateChild: [themeGuard('primeng')]
  },
  {
    path: 'warranty',
    loadChildren: () => import('./warranty-module/warranty.routes').then(m => m.WARRANTY_ROUTES),
    canActivate: [ProtectedGuard, RoleAccessGuard],
    canActivateChild: [themeGuard('legacy')],
    data: {
      module: ModuleRoutes.Warranty
    },
    resolve: { store: warrantyResolver, HomebuildingResolver }
  },
  {
    path: 'administration',
    loadChildren: () => import('./administration-module/administration.routes').then(m => m.ADMINISTRATION_ROUTES),
    canActivate: [AdminGuard, RoleAccessGuard],
    canActivateChild: [themeGuard('primeng')],
    data: {
      module: ModuleRoutes.UserManagement
    }
  },
  {
    path: 'scheduler',
    loadChildren: () => import('./scheduler-module/scheduler.routes').then(m => m.SCHEDULER_ROUTES),
    canActivate: [ProtectedGuard, RoleAccessGuard],
    canActivateChild: [themeGuard('legacy')],
    data: {
      module: ModuleRoutes.Scheduler
    }
  },
  {
    path: 'home-building',
    loadChildren: () => import('./home-building-module/home-building.routes').then(m => m.HOME_BUILDING_ROUTES),
    canActivate: [ProtectedGuard, RoleAccessGuard],
    canActivateChild: [themeGuard('legacy')],
    data: {
      module: ModuleRoutes.HomeBuilding
    },
    resolve: { store: HomebuildingResolver }
  },

  {
    path: 'accounting',
    loadChildren: () => import('./accounting-module/accounting-module.routes').then(m => m.AccountingModuleRoutes),
    canActivate: [ProtectedGuard, RoleAccessGuard],
    canActivateChild: [themeGuard('legacy')],
    data: {
      module: ModuleRoutes.Accounting
    }
  },

  {
    path: 'sales',
    loadChildren: () => import('./sales-module/sales.routes').then(m => m.SalesModuleRoutes),
    canActivate: [ProtectedGuard, RoleAccessGuard],
    canActivateChild: [themeGuard('legacy')],
    data: {
      module: ModuleRoutes.Sales
    },
    resolve: { store: salesResolver }
  },

  {
    path: 'dsm',
    loadChildren: () => import('./dsm-module/dsm.routes').then(m => m.DsmModuleRoutes),
    canActivate: [ProtectedGuard, RoleAccessGuard],
    canActivateChild: [themeGuard('primeng', 'dsm-theme')],
    data: {
      module: ModuleRoutes.Dsm
    }
  },
  {
    path: 'buyer',
    loadChildren: () => import('./buyer-module/buyer.routes').then(m => m.BuyerModuleRoutes),
    canActivate: [ProtectedGuard],
    canActivateChild: [themeGuard('legacy')],
    data: {
      module: ModuleRoutes.Buyer
    }
  },
  {
    path: 'executives',
    loadChildren: () =>
      import('./executive-console-module/executive-console.routes').then(m => m.ExecutiveConsoleModuleRoutes),
    canActivate: [ProtectedGuard, RoleAccessGuard],
    canActivateChild: [themeGuard('primeng')],
    data: {
      module: ModuleRoutes.ExecutiveConsole
    },
    resolve: { store: executiveConsoleResolver }
  },
  {
    path: 'new',
    component: UnauthLayoutComponent,
    resolve: {
      public: publicPagesResolver
    },
    canActivate: [themeGuard('legacy')],
    children: [
      {
        path: ':salesOfficeId/public',
        component: QrQuestionComponent,
        title: 'User Identification'
      },
      {
        path: ':salesOfficeId/prospect',
        component: PublicProspectComponent,
        title: 'New Prospect Registration'
      },
      {
        path: ':salesOfficeId/realtor',
        component: PublicRealtorComponent,
        title: 'New Realtor Registration'
      }
    ]
  },

  {
    path: '**',
    component: PageNotFoundComponent,
    canActivate: [themeGuard('legacy')],
    title: 'Page Not Found',
    data: { type: '404' }
  }
];
